<template>
  <div>
    <b-row class="justify-content-between mb-3">
      <b-col class="d-flex align-items-center">
        <h2 class="mb-0">
          <b-badge variant="dark">Votes par présence physique</b-badge>
        </h2>
      </b-col>
      <b-col cols="3" sm="6" md="3">
        <label>Rechercher</label>
        <b-form-input v-model="search" placeholder="Tapez pour rechercher" @input="filter" />
      </b-col>
    </b-row>

    <appLockOverlay :condition="showOverlay">
      <template v-slot:lock-overlay-content>
        <b-table
          :style="{
            pointerEvents: showOverlay ? 'none' : 'unset',
          }"
          bordered
          class="agv-table"
          :fields="fields"
          :items="tableData"
          :tbody-tr-class="detailsClassFn"
        >
          <template #head(for)>
            {{ $t("meetings.votingTable.for") }}
            <b-button
              size="sm"
              class="text-nowrap"
              variant="light"
              @click="selectAllVotes(voteTypes.for)"
            >
              {{ $t("general.Tous") }} <font-awesome-icon icon="check-square" />
            </b-button>
          </template>
          <template #head(against)>
            {{ $t("meetings.votingTable.against") }}
            <b-button
              size="sm"
              class="text-nowrap"
              variant="light"
              @click="selectAllVotes(voteTypes.against)"
            >
              {{ $t("general.Tous") }} <font-awesome-icon icon="check-square" />
            </b-button>
          </template>
          <template #head(abs)>
            {{ $t("meetings.votingTable.abs") }}
            <b-button
              size="sm"
              class="text-nowrap"
              variant="light"
              @click="selectAllVotes(voteTypes.abstain)"
            >
              {{ $t("general.Tous") }} <font-awesome-icon icon="check-square" />
            </b-button>
          </template>
          <template v-slot:cell(building_owner.lastname)="row">
            <div>
              <appVotingsMandateName
                :current-resolution="currentResolutionData"
                :main-mandate="row.item"
              />
            </div>
          </template>
          <template v-slot:cell(representant)="row">
            <div>
              <appVotingsMandateRepresentant :main-mandate="row.item" />
            </div>
          </template>
          <template v-slot:cell(for)="row">
            <div>
              <appVotingsMandateVote
                v-if="getTantiemes(row.item) != 0"
                :type="voteTypes.for"
                :main-mandate="row.item"
                :current-resolution="currentResolutionData"
                :mandates="mandates"
                :disabled="disableVoting(row.item.$disabled, getTantiemes(row.item))"
              />
            </div>
          </template>
          <template v-slot:cell(against)="row">
            <div>
              <appVotingsMandateVote
                v-if="getTantiemes(row.item) != 0"
                :type="voteTypes.against"
                :main-mandate="row.item"
                :current-resolution="currentResolutionData"
                :mandates="mandates"
                :disabled="disableVoting(row.item.$disabled, getTantiemes(row.item))"
              />
            </div>
          </template>
          <template v-slot:cell(abs)="row">
            <div>
              <appVotingsMandateVote
                v-if="getTantiemes(row.item) != 0"
                :type="voteTypes.abstain"
                :main-mandate="row.item"
                :current-resolution="currentResolutionData"
                :mandates="mandates"
                :disabled="disableVoting(row.item.$disabled, getTantiemes(row.item))"
              />
            </div>
          </template>
        </b-table>
      </template>
    </appLockOverlay>
  </div>
</template>

<script>
import ResolutionVoteMW from "@/middlewares/ResolutionVote";
import { meetingMinutesNames } from "@/constants/meeting.js";
import minutesMixin from "@/mixins/minutes";
import MandateORM from "@/store/orm-axios/models/Mandate";

export default {
  components: {
    appLockOverlay: () => import("@/components/snippets/LockOverlay"),
    appVotingsMandateName: () =>
      import("@/components/b-table-components/votingTable/VotingsMandateName"),
    appVotingsMandateRepresentant: () =>
      import("@/components/b-table-components/votingTable/VotingsMandateRepresentant"),
    appVotingsMandateVote: () =>
      import("@/components/b-table-components/votingTable/VotingsMandateVote"),
  },
  mixins: [minutesMixin],
  props: {
    currentResolution: {
      type: Object,
      default: () => {
        return {};
      },
    },
    mandates: {
      type: Array,
      default: () => {
        return {};
      },
    },
    showOverlay: {
      type: Boolean,
      default: false,
    },
    isResolutionBlocked: {
      type: Boolean,
      default: true,
    },
    meetingId: {
      type: String,
      required: true,
    },
    minutes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    showMinutesData: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      meetingMinutesNames,
      currentResolutionData: {},
      tableData: [],
      fields: [
        {
          key: "building_owner.lastname",
          sortable: true,
          label: this.$t("meetings.votingTable.name"),
        },
        {
          key: "representant",
          label: this.$t("meetings.votingTable.representant"),
        },
        {
          key: "for",
          label: this.$t("meetings.votingTable.for"),
        },
        {
          key: "against",
          label: this.$t("meetings.votingTable.against"),
        },
        {
          key: "abs",
          label: this.$t("meetings.votingTable.abs"),
        },
      ],
      search: "",
      currentResolutionVoteEvent: null,
    };
  },
  watch: {
    mandates() {
      this.prepareTableData();
    },
    currentResolution(newVal, oldVal) {
      if (!this.$_.isEqual(newVal, oldVal)) {
        this.currentResolutionData = newVal;
        if (
          !this.isResolutionBlocked &&
          !this.$_.isEqual(newVal.voting_category_id, oldVal.voting_category_id)
        ) {
          this.changeVotesOnCurrentResolutionChange();
        }
        this.prepareTableData();
      }
    },
    showMinutesData(newValue, oldValue) {
      if (newValue != oldValue) {
        this.prepareTableData();
      }
    },
  },
  created() {
    this.prepareTableData();
    this.currentResolutionData = this.currentResolution;
  },
  methods: {
    detailsClassFn(item, rowType) {
      // item: the row's item data
      // rowType: a string describing the `<tr>` type
      let tantiemes = this.getUsedMandateTantiemesByVotingCategory(
        item.id,
        this.currentResolutionData.voting_category_id
      );
      if (item.$disabled) {
        // If it is a row-details row, add the the following classes
        // to the `<tr>` element
        // display none on xs and up, display table-row md and up
        return "bg-secondary not-editable";
      } else if (tantiemes == 0) {
        return "bg-warning-light not-editable";
      }
    },
    async selectAllVotes(type) {
      try {
        this.$store.state.showLoaderOverlay = true;
        var voteType = type;

        // In addition, mandates with 0 tantiemes will not vote (DEV-4060)
        var presentOrRepresentedMandates = this.$_.filter(this.mandates, mandate => {
          return (
            (mandate.presence_status == this.mandatePresenceStatuses.physical ||
              mandate.presence_status == this.mandatePresenceStatuses.pouvoir) &&
            this.getUsedMandateTantiemesByVotingCategory(
              mandate.mandate_id ? mandate.mandate_id : mandate.id, // ???: because of confusing notation in minutes
              this.currentResolutionData.voting_category_id
            ) != 0
          );
        });

        // prepare post object for bulk vote
        var bulkVotePostObj = this.prepareBulkVotePostObj(
          voteType,
          presentOrRepresentedMandates,
          this.currentResolutionData.id
        );

        await ResolutionVoteMW.BULK_VOTE(bulkVotePostObj)
          .then(async res => {
            // console.log(res);
            await ResolutionVoteMW.FETCH(this.currentResolutionData.id);
          })
          .catch(async err => {
            console.error(err);
            await ResolutionVoteMW.FETCH(this.currentResolutionData.id);
          });
        this.$store.state.showLoaderOverlay = false;
      } catch {
        this.$store.state.showLoaderOverlay = false;
      }
    },
    sortedMandates(mandates) {
      return this.$_.sortBy(mandates, mandate => {
        return this.getPersonName({ personId: mandate.building_owner_id });
      });
    },
    filter(e) {
      let result = this.$_.filter(this.mandates, mandate => {
        let buildingOwner = this.getPersonName({ personId: mandate.building_owner_id });
        let representantOwner = this.getPersonName({ personId: mandate.representant_owner_id });
        return (
          buildingOwner.toLowerCase().includes(e) || representantOwner.toLowerCase().includes(e)
        );
      });
      this.tableData = this.sortedMandates(result);
    },
    getTantiemes(item) {
      return this.getUsedMandateTantiemesByVotingCategory(
        item.id,
        this.currentResolution.voting_category_id
      );
    },
    disableVoting(disable, tantiemes) {
      if (disable && tantiemes == 0) {
        return true;
      } else if (tantiemes == 0) {
        return true;
      } else {
        return false;
      }
    },
    // NOTE: In case of changing voting_category and Mandate has 0 tantiemes, votes has to be null
    async changeVotesOnCurrentResolutionChange() {
      try {
        this.$store.state.showLoaderOverlay = true;

        var mandatesWhichCannotVote = this.$_.filter(this.mandates, mandate => {
          return (
            (mandate.presence_status == this.mandatePresenceStatuses.physical ||
              mandate.presence_status == this.mandatePresenceStatuses.pouvoir) &&
            this.getUsedMandateTantiemesByVotingCategory(
              mandate.mandate_id ? mandate.mandate_id : mandate.id,
              this.currentResolutionData.voting_category_id
            ) == 0
          );
        });

        if (mandatesWhichCannotVote.length > 0) {
          // prepare post object for bulk vote
          var bulkVotePostObj = this.prepareBulkVotePostObj(
            null,
            mandatesWhichCannotVote,
            this.currentResolutionData.id
          );

          await ResolutionVoteMW.BULK_VOTE(bulkVotePostObj)
            .then(async res => {
              // console.log(res);
              await ResolutionVoteMW.FETCH(this.currentResolutionData.id);
            })
            .catch(async err => {
              console.error(err);
              await ResolutionVoteMW.FETCH(this.currentResolutionData.id);
            });
        }

        this.$store.state.showLoaderOverlay = false;
      } catch {
        this.$store.state.showLoaderOverlay = false;
      }
    },
    getCurrentResolutionVoteEvent() {
      return this.minutes.find(
        item =>
          item.event == this.meetingMinutesNames.resolution_vote &&
          item.resolution_id == this.currentResolution.id
      );
    },
    getMandatesBasedOnMinutesEvents() {
      const minutesMandatesEvents = this.getMinutesMandatesForResolution(this.currentResolution.id);
      const mandates = MandateORM.getters("getMandatesByMeeting")(this.meetingId);
      let mandatesToReturn = [];
      for (const mandate of mandates) {
        const mandateLastMinuteEvent = this.getMandateLastUpdateFromMinutes(
          minutesMandatesEvents,
          mandate.id
        );
        if (
          mandateLastMinuteEvent &&
          mandateLastMinuteEvent.new_presence_status &&
          (mandateLastMinuteEvent.new_presence_status == this.mandatePresenceStatuses.physical ||
            mandateLastMinuteEvent.new_presence_status == this.mandatePresenceStatuses.pouvoir)
        ) {
          let cloneMandate = { ...mandate };
          cloneMandate.presence_status = mandateLastMinuteEvent.new_presence_status;
          mandatesToReturn.push(cloneMandate);
        }
      }
      return mandatesToReturn;
    },
    prepareTableData() {
      this.currentResolutionVoteEvent = this.getCurrentResolutionVoteEvent();
      if (this.currentResolutionVoteEvent && this.showMinutesData) {
        const mandates = this.getMandatesBasedOnMinutesEvents();
        this.tableData = this.sortedMandates(mandates);
      } else {
        this.tableData = this.sortedMandates(this.mandates);
      }
    },
  },
};
</script>
